"use client";

import {
  getDesktop,
  getMargins,
  getMobile,
  getPaddings,
  getRounded,
  ownRadiusStyle,
} from "@/utils/theme-helper";
import { twMerge } from "tailwind-merge";
import type { SurfaceStyle, TypographyAppearance } from "@/cms/appearance";
import { surfaceStyle, typographyStyle } from "@/utils/visual-style";
import { withRichTextRuleColours } from "@/utils/rich-text-rule";

export interface TypographyComponentProps {
  text: string;
  surface?: SurfaceStyle;
  typography?: TypographyAppearance;
  size: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "4xl";
  bgColor?: string;
  textColor: string;
  padding: string;
  margin: string;
  align: "center";
  height: string;
  width: string;
  rounded: string;
  letterType: "normal" | "uppercase" | "lowercase" | "capitalize";
  /** Set by `mergeTypographySettings` on look-system websites, where `rounded` may be an exact length. */
  lookSystem?: boolean;
}

const TypographyComponent: React.FC<{
  config: TypographyComponentProps;
}> = ({ config: typography }) => {
  const mobileWidth = getMobile("none", typography.width);
  const desktopWidth = getDesktop("none", typography.width);
  const mobileHeight = getMobile("none", typography.height);
  const desktopHeight = getDesktop("none", typography.height);

  return (
    <div
      style={
        {
          color: typography.textColor,
          backgroundColor: typography.surface ? undefined : typography.bgColor,
          ...surfaceStyle(typography.surface, typography.bgColor),
          ...typographyStyle(typography.typography),
          textAlign: typography.align,
          "--mobile-width": mobileWidth,
          "--desktop-width": desktopWidth,
          "--mobile-height": mobileHeight,
          "--desktop-height": desktopHeight,
          ...ownRadiusStyle(typography.rounded, typography.lookSystem),
        } as React.CSSProperties
      }
      dangerouslySetInnerHTML={{
        __html: withRichTextRuleColours(typography.text),
      }}
      className={twMerge(
        `styled-description ${typography.typography?.fontSize ? "" : `homepage-text-${typography?.size ?? "sm"}`} ${getMargins(typography.margin)} ${getPaddings(typography.padding)} ${mobileWidth !== "none" ? "w-[var(--mobile-width)]" : ""}`,
        `${desktopWidth !== "none" ? "md:w-[var(--desktop-width)]" : ""} ${mobileHeight !== "none" ? "h-[var(--mobile-height)]" : ""} ${desktopHeight !== "none" ? "md:h-[var(--desktop-height)]" : ""} ${getRounded(typography.rounded, typography.lookSystem)}`,
        "overflow-hidden",
        typography.surface && "re-surface",
        typography.typography && "re-type",
        typography.typography?.columnCount && "re-type-columns",
        typography.typography?.fontSize && "re-type-size",
        typography.typography?.fontWeight !== undefined && "re-type-weight",
        (typography.typography?.paragraphSpacing ||
          typography.typography?.paragraphMarginTop ||
          typography.typography?.paragraphMaxWidth) &&
          "re-type-paragraphs",
        (typography.letterType ?? "normal") !== "normal"
          ? typography.letterType
          : "",
      )}
    ></div>
  );
};

export default TypographyComponent;
